// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import debounce from 'lodash/debounce';

import {QueryReason} from 'common/report/util/query-tracking/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {END_OF_TODAY, START_OF_TODAY} from 'common/widgets/date-range-picker/models/presets';
import {DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {parseJsUrl} from 'common/report/util/url';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {promisifyModal} from 'common/components/modal/utils';
import {columnNamesToSelectedProperties, selectedPropertiesToColumnNames} from 'common/report/events/utils';
import {inspectorClient, InspectorEvent} from 'common/report/queries/query-api/inspector';
import {formatDefaultTrackData, TrackEvents} from 'common/report/events/tracking';
import {InspectorAppState, PropertyKeyFilter, ViewFormat} from 'common/report/events/types';
import {ResourceType} from 'common/report/constants';
import {keyEventSwitcher} from 'common/util/keyboard';
import {IronContextRegistry} from 'common/report/context-registry';

import {template} from './template';
import './index.styl';

const DEFAULT_EVENT_SEARCH_PROPS = [`$event_name`, `$distinct_id`, `$email`];
const DEFAULT_USER_SEARCH_PROPS = [`$email`, `$name`, `$city`, `$first_name`, `$last_name`];
export const INSPECTOR_DEFAULT_STATE = {
  bookmark: ``,
  colWidths: {},
  dateRange: {},
  entries: [],
  events: [],
  totalCount: null,
  error: null,
  filters: [],
  filtersOperator: `and`,

  limit: 100,
  loading: false,
  loadingBookmarkUrl: false,
  loadingMore: false,

  showQueryBuilder: true,
  lastRefreshed: new Date().toString(),
  hasReceivedEvents: false,

  propertyKeyFilter: PropertyKeyFilter.AllProperties,
  viewFormat: ViewFormat.Table,
  search: ``,
  isSearchInputExpanded: false,
  selectedProperties: [],
  sentinelEvent: null,
  selectedEvent: null,
  selectedDistinctId: null,
  selectedRowIdx: null,
  isQuerySamplingEnabled: false,
};

export type EventsExportJsxProps = {
  props: {
    url: string;
  };
  attrs: {
    url: string;
  };
};

interface SidebarEventsState {
  pinnedProperties: string[];
  url: string;
  selectedEvent: InspectorEvent;
}

export class EventsSidebarWidget extends Component<
  InspectorAppState & SidebarEventsState,
  EventsExportJsxProps,
  unknown,
  unknown,
  IronContextRegistry
> {
  public static get attrsSchema(): AttrsSchema<EventsExportJsxProps['attrs']> {
    return {
      url: `string`,
    };
  }

  disconnectedCallback() {
    this.removeKeyHandler();
  }

  removeKeyHandler() {
    document.removeEventListener(`keydown`, this.operatingSystemKeySwitcher);
  }

  /** Changes keys based on operating system we're using and triggers toggleCollapsed */
  operatingSystemKeySwitcher = (ev: KeyboardEvent) => {
    return keyEventSwitcher(
      {
        [`ArrowRight`]: () => {
          this.handleSelect(Math.min(this.state.selectedRowIdx + 1, this.state.events.length - 1));
        },
        [`ArrowLeft`]: () => {
          this.handleSelect(Math.max(this.state.selectedRowIdx - 1, 0));
        },
      },
      {stopPropagation: true, usableInInputs: true},
    )(ev);
  };

  get defaultState() {
    return {
      ...INSPECTOR_DEFAULT_STATE,
      url: null,
      selectedRow: null,
      pinnedProperties: [],
      ...{
        dateRange: {
          from: START_OF_TODAY,
          to: END_OF_TODAY,
          type: DateRangeType.Between,
        } as FullLengthDateRange,
      },
    };
  }

  set url(url: string) {
    let parsedJsUrl;
    try {
      const hash = url.split(`#`)[1];
      parsedJsUrl = parseJsUrl(hash);
    } catch (err) {
      this.update({
        url,
        error: err.message,
      });
      return;
    }
    this.update({
      url,
      ...parsedJsUrl.data,
      selectedEvent: null,
    });
    this.queryData(QueryReason.BookmarkView);
  }

  handleSelect(idx: number) {
    this.update({
      selectedEvent: this.state.events[idx],
      selectedRowIdx: idx,
    });
    document.addEventListener(`keydown`, this.operatingSystemKeySwitcher);
  }

  handleRowClick(row: InspectorEvent) {
    this.update({
      selectedEvent: row,
    });
  }

  unselectEvent() {
    this.update({
      selectedEvent: null,
    });
    this.removeKeyHandler();
  }

  updateViewFormat(viewFormat: ViewFormat) {
    this.update({viewFormat});
  }

  onSearchChange(search: string) {
    this.debouncedSearch(search);
  }

  private debouncedSearch = debounce((search: string) => {
    this.update({
      search,
    });
    this.queryData(QueryReason.QbOtherUpdate);
  }, 1000);

  get config() {
    return {
      template,
      contexts: [`i18n` as const],
      defaultState: this.defaultState,
      helpers: {
        handleRowClick: (row: any) => this.handleRowClick(row),
        handleSelect: (idx: number) => this.handleSelect(idx),
        handleUnselect: () => this.unselectEvent(),
        onSearchChange: (search: string) => this.onSearchChange(search),
      },
    };
  }

  private async queryEvents(params) {
    const startTime = new Date().getTime();
    try {
      const {events} = await inspectorClient.queryData(params);
      this.update({
        events,
      });
      this.update({
        lastRefreshed: new Date().toString(),
        ...(events.length > 0 ? {hasReceivedEvents: true} : {}),
        loading: false,
        loadingMore: false,
        selectedEvent: null,
      });
    } catch (err) {
      this.update({error: err.message, loading: false});
    } finally {
      const timeElapsed = new Date().getTime() - startTime;
      mixpanel.track(TrackEvents.QueryComplete, {
        ...formatDefaultTrackData(this.state),
        'Query Error': this.state.error,
        'Time elapsed': timeElapsed / 1000,
        Mode: `raw`,
      });
    }
  }

  private async queryData(queryReason: QueryReason) {
    this.update({error: null, loading: true});

    const {entries, filters, filtersOperator, dateRange, search = ``, selectedProperties, limit} = this.state;

    // start querying
    const params = {
      bookmark: {entries, filters, filtersOperator, dateRange},
      limit,
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      queryReason,
      search: search.trim(),
      searchProperties: [
        ...columnNamesToSelectedProperties(
          Array.from(new Set([...selectedPropertiesToColumnNames(selectedProperties), ...DEFAULT_EVENT_SEARCH_PROPS])),
        ),
        ...columnNamesToSelectedProperties(DEFAULT_USER_SEARCH_PROPS, ResourceType.User),
      ],
      pagingWindow: 30,
    };
    await this.queryEvents(params);
  }
}

export async function openEventsSidebar(props: EventsExportJsxProps['props']): Promise<boolean> {
  return promisifyModal({
    tagName: `mp-event-inspector-sidebar`,
    props,
    attrs: props,
    closeEvents: [`close`],
  });
}

export const InspectorAppLiteEl = defineAndMakeJsxFactory<EventsExportJsxProps>(
  `mp-event-inspector-sidebar`,
  EventsSidebarWidget,
);
export const InspectorAppLiteReact = panel2react<EventsExportJsxProps>(
  `mp-event-inspector-sidebar`,
  EventsSidebarWidget,
);
